import { useState, useEffect } from 'react';
import { RefreshCw, AlertTriangle, Power, Upload, Loader2, Image as ImageIcon, CheckSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { checkMaintenanceStatus, setMaintenanceMode, MaintenanceStatus, fetchRegistrationCheckboxes, updateRegistrationCheckboxes, RegistrationCheckboxes } from '../../../lib/prompt-admin-api';
import { uploadViaEdgeFunction } from '../../../lib/gcs-client';
import { supabase } from '../../../lib/supabase-client';

export function AdminSettingsTab() {
    const [maintenance, setMaintenance] = useState<MaintenanceStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [customMessage, setCustomMessage] = useState('');

    // Wysko Logo
    const [wyskoLogoUrl, setWyskoLogoUrl] = useState<string | null>(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [loadingLogo, setLoadingLogo] = useState(false);

    // Sample Image for watermark preview
    const [sampleImageUrl, setSampleImageUrl] = useState<string | null>(null);
    const [uploadingSample, setUploadingSample] = useState(false);
    const [loadingSample, setLoadingSample] = useState(false);

    // Registration checkboxes
    const [checkboxSettings, setCheckboxSettings] = useState<RegistrationCheckboxes | null>(null);
    const [loadingCheckboxes, setLoadingCheckboxes] = useState(false);
    const [savingCheckboxes, setSavingCheckboxes] = useState(false);
    const [tosText, setTosText] = useState('');
    const [tosUrl, setTosUrl] = useState('');
    const [tosLinkLabel, setTosLinkLabel] = useState('');
    const [newsletterText, setNewsletterText] = useState('');

    useEffect(() => {
        loadSettings();
        loadWyskoLogo();
        loadSampleImage();
        loadCheckboxSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const status = await checkMaintenanceStatus();
            setMaintenance(status);
            setCustomMessage(status.message || '');
        } catch (error) {
            toast.error('Nie udało się załadować ustawień');
        } finally {
            setLoading(false);
        }
    };

    const loadWyskoLogo = async () => {
        try {
            setLoadingLogo(true);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            // Try to get signed URL for existing logo
            const logoPath = 'branding/wysko-watermark.png';
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/server/gcs/sign-urls`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    items: [{ bucket: 'wysko-assets', path: logoPath }]
                })
            });

            if (response.ok) {
                const result = await response.json();
                if (result.data && result.data[logoPath]) {
                    setWyskoLogoUrl(result.data[logoPath]);
                }
            }
        } catch (error) {
            console.error('Failed to load Wysko logo:', error);
        } finally {
            setLoadingLogo(false);
        }
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return;

        const file = e.target.files[0];

        // Validation
        if (file.size > 2 * 1024 * 1024) {
            toast.error('Plik jest za duży (max 2MB)');
            return;
        }

        if (!['image/png', 'image/jpeg'].includes(file.type)) {
            toast.error('Dozwolone są tylko pliki PNG i JPG');
            return;
        }

        try {
            setUploadingLogo(true);

            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                toast.error('Brak sesji');
                return;
            }

            // Upload to fixed path: wysko-assets/branding/wysko-watermark.png
            const uploadResult = await uploadViaEdgeFunction(
                file,
                'wysko-assets',
                session.user.id,
                undefined, // no propertyId
                'branding/wysko-watermark.png' // custom path
            );

            if (uploadResult.gcsPath) {
                toast.success('Logo Wysko zostało wgrane');
                await loadWyskoLogo(); // Reload to get signed URL
            }
        } catch (error) {
            console.error('Logo upload failed:', error);
            toast.error('Nie udało się wgrać logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const loadSampleImage = async () => {
        try {
            setLoadingSample(true);
            const { data: { session } } = await supabase.auth.getSession();
            if (!session) return;

            // Try to get signed URL for existing sample image
            const samplePath = 'branding/watermark-sample.jpg';
            const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/server/gcs/sign-urls`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session.access_token}`
                },
                body: JSON.stringify({
                    items: [{ bucket: 'wysko-assets', path: samplePath }]
                })
            });

            if (response.ok) {
                const result = await response.json();
                if (result.data && result.data[samplePath]) {
                    setSampleImageUrl(result.data[samplePath]);
                }
            }
        } catch (error) {
            console.error('Failed to load sample image:', error);
        } finally {
            setLoadingSample(false);
        }
    };

    const handleSampleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || !e.target.files[0]) return;

        const file = e.target.files[0];

        // Validation
        if (file.size > 5 * 1024 * 1024) {
            toast.error('Plik jest za duży (max 5MB)');
            return;
        }

        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
            toast.error('Dozwolone są tylko pliki PNG i JPG');
            return;
        }

        try {
            setUploadingSample(true);

            const { data: { session } } = await supabase.auth.getSession();
            if (!session) {
                toast.error('Brak sesji');
                return;
            }

            // Upload to fixed path: wysko-assets/branding/watermark-sample.jpg
            const uploadResult = await uploadViaEdgeFunction(
                file,
                'wysko-assets',
                session.user.id,
                undefined, // no propertyId
                'branding/watermark-sample.jpg' // custom path
            );

            if (uploadResult.gcsPath) {
                toast.success('Przykładowe zdjęcie zostało wgrane');
                await loadSampleImage(); // Reload to get signed URL
            }
        } catch (error) {
            console.error('Sample image upload failed:', error);
            toast.error('Nie udało się wgrać zdjęcia');
        } finally {
            setUploadingSample(false);
        }
    };

    const handleToggleMaintenance = async () => {
        if (!maintenance) return;

        try {
            setSaving(true);
            const newEnabled = !maintenance.enabled;

            const result = await setMaintenanceMode(newEnabled, customMessage || undefined);

            setMaintenance({
                enabled: result.enabled,
                message: result.message,
                started_at: result.started_at
            });

            toast.success(
                newEnabled
                    ? 'Tryb konserwacji WŁĄCZONY - użytkownicy nie mają dostępu'
                    : 'Tryb konserwacji WYŁĄCZONY - aplikacja dostępna'
            );
        } catch (error) {
            toast.error('Nie udało się zmienić trybu konserwacji');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveMessage = async () => {
        if (!maintenance) return;

        try {
            setSaving(true);
            await setMaintenanceMode(maintenance.enabled, customMessage);
            toast.success('Wiadomość została zapisana');
        } catch (error) {
            toast.error('Nie udało się zapisać wiadomości');
        } finally {
            setSaving(false);
        }
    };

    const loadCheckboxSettings = async () => {
        try {
            setLoadingCheckboxes(true);
            const data = await fetchRegistrationCheckboxes();
            setCheckboxSettings(data);
            setTosText(data.tos_text);
            setTosUrl(data.tos_url);
            setTosLinkLabel(data.tos_link_label);
            setNewsletterText(data.newsletter_text);
        } catch (error) {
            toast.error('Nie udało się załadować ustawień checkboxów');
        } finally {
            setLoadingCheckboxes(false);
        }
    };

    const handleSaveCheckboxes = async () => {
        try {
            setSavingCheckboxes(true);
            const result = await updateRegistrationCheckboxes({
                tos_text: tosText,
                tos_url: tosUrl,
                tos_link_label: tosLinkLabel,
                newsletter_text: newsletterText
            });
            setCheckboxSettings({
                tos_text: tosText,
                tos_url: tosUrl,
                tos_link_label: tosLinkLabel,
                newsletter_text: newsletterText
            });
            toast.success('Teksty checkboxów zostały zapisane');
        } catch (error) {
            toast.error('Nie udało się zapisać ustawień');
        } finally {
            setSavingCheckboxes(false);
        }
    };

    const checkboxesChanged = checkboxSettings && (
        tosText !== checkboxSettings.tos_text ||
        tosUrl !== checkboxSettings.tos_url ||
        tosLinkLabel !== checkboxSettings.tos_link_label ||
        newsletterText !== checkboxSettings.newsletter_text
    );

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-8 max-w-2xl">
            <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold">Ustawienia aplikacji</h2>
                <Button variant="outline" size="sm" onClick={loadSettings}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    Odśwież
                </Button>
            </div>

            {/* Maintenance Mode Section */}
            <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${maintenance?.enabled ? 'bg-red-500/10' : 'bg-muted'}`}>
                        <Power className={`w-6 h-6 ${maintenance?.enabled ? 'text-red-500' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-medium">Tryb konserwacji</h3>
                                <p className="text-sm text-muted-foreground mt-1">
                                    Gdy włączony, wszyscy użytkownicy (oprócz admina) zobaczą stronę przerwy technicznej
                                </p>
                            </div>
                            <Switch
                                checked={maintenance?.enabled || false}
                                onCheckedChange={handleToggleMaintenance}
                                disabled={saving}
                            />
                        </div>

                        {maintenance?.enabled && maintenance?.started_at && (
                            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-red-500" />
                                <span className="text-sm text-red-500">
                                    Tryb konserwacji aktywny od: {new Date(maintenance.started_at).toLocaleString('pl-PL')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Custom Message */}
                <div className="space-y-3 pt-4 border-t border-border">
                    <label className="text-sm font-medium">Wiadomość dla użytkowników</label>
                    <Textarea
                        value={customMessage}
                        onChange={(e) => setCustomMessage(e.target.value)}
                        placeholder="Wysko wraca za moment. Aktualizujemy system..."
                        rows={3}
                    />
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSaveMessage}
                        disabled={saving || customMessage === maintenance?.message}
                    >
                        Zapisz wiadomość
                    </Button>
                </div>
            </div>

            {/* Wysko Branding Section */}
            <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-muted">
                        <ImageIcon className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-medium">Logo Wysko (znak wodny)</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Logo używane jako znak wodny "Created with Wysko" na wygenerowanych zdjęciach
                        </p>
                    </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-border">
                    {/* Current Logo Preview */}
                    {loadingLogo ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                    ) : wyskoLogoUrl ? (
                        <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                            <img
                                src={wyskoLogoUrl}
                                alt="Wysko Logo"
                                className="h-16 w-auto object-contain"
                            />
                            <div className="text-sm text-muted-foreground">
                                Obecne logo
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 bg-muted/50 rounded-lg text-center text-sm text-muted-foreground">
                            Brak wgranego logo
                        </div>
                    )}

                    {/* Upload Section */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">
                            {wyskoLogoUrl ? 'Zamień logo' : 'Wgraj logo'}
                        </Label>
                        <div className="flex items-center gap-3">
                            <Input
                                type="file"
                                className="hidden"
                                id="wysko-logo-upload"
                                accept="image/png, image/jpeg"
                                onChange={handleLogoUpload}
                                disabled={uploadingLogo}
                            />
                            <Button
                                variant="outline"
                                size="sm"
                                asChild
                                disabled={uploadingLogo}
                            >
                                <label htmlFor="wysko-logo-upload" className="cursor-pointer">
                                    {uploadingLogo ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Wgrywanie...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            {wyskoLogoUrl ? 'Zmień' : 'Wgraj'} logo
                                        </>
                                    )}
                                </label>
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            PNG lub JPG, max 2MB. Zalecane: 200-300px szerokości, przezroczyste tło.
                        </p>
                    </div>
                </div>
            </div>

            {/* Watermark Sample Image Section */}
            <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-muted">
                        <ImageIcon className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-medium">Przykładowe zdjęcie (watermark preview)</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Zdjęcie używane w podglądzie znaku wodnego gdy użytkownik nie ma jeszcze generacji
                        </p>
                    </div>
                </div>

                <div className="space-y-4 pt-4 border-t border-border">
                    {/* Current Sample Preview */}
                    {loadingSample ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin" />
                        </div>
                    ) : sampleImageUrl ? (
                        <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
                            <img
                                src={sampleImageUrl}
                                alt="Sample Image"
                                className="h-24 w-auto object-contain"
                            />
                            <div className="text-sm text-muted-foreground">
                                Obecne zdjęcie
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 bg-muted/50 rounded-lg text-center text-sm text-muted-foreground">
                            Brak wgranego zdjęcia
                        </div>
                    )}

                    {/* Upload Section */}
                    <div className="space-y-3">
                        <Label className="text-sm font-medium">
                            {sampleImageUrl ? 'Zamień zdjęcie' : 'Wgraj zdjęcie'}
                        </Label>
                        <div className="flex items-center gap-3">
                            <Input
                                type="file"
                                className="hidden"
                                id="sample-image-upload"
                                accept="image/png, image/jpeg"
                                onChange={handleSampleUpload}
                                disabled={uploadingSample}
                            />
                            <Button
                                variant="outline"
                                size="sm"
                                asChild
                                disabled={uploadingSample}
                            >
                                <label htmlFor="sample-image-upload" className="cursor-pointer">
                                    {uploadingSample ? (
                                        <>
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                            Wgrywanie...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4 mr-2" />
                                            {sampleImageUrl ? 'Zmień' : 'Wgraj'} zdjęcie
                                        </>
                                    )}
                                </label>
                            </Button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            PNG lub JPG, max 5MB. Zalecane: zdjęcie pokoju w dobrej rozdzielczości.
                        </p>
                    </div>
                </div>
            </div>

            {/* Registration Checkboxes Section */}
            <div className="bg-card border border-border rounded-xl p-6 space-y-6">
                <div className="flex items-start gap-4">
                    <div className="p-3 rounded-lg bg-muted">
                        <CheckSquare className="w-6 h-6 text-muted-foreground" />
                    </div>
                    <div className="flex-1">
                        <h3 className="text-lg font-medium">Teksty checkboxów rejestracji</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                            Treści wyświetlane przy checkboxach na stronie rejestracji
                        </p>
                    </div>
                </div>

                {loadingCheckboxes ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="w-6 h-6 animate-spin" />
                    </div>
                ) : (
                    <div className="space-y-4 pt-4 border-t border-border">
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Tekst regulaminu</Label>
                            <Input
                                value={tosText}
                                onChange={(e) => setTosText(e.target.value)}
                                placeholder="Akceptuję {regulamin}"
                            />
                            <p className="text-xs text-muted-foreground">
                                Użyj <code className="bg-muted px-1 rounded">{'{regulamin}'}</code> jako placeholder dla linku do regulaminu
                            </p>
                        </div>

                        <div className="space-y-3">
                            <Label className="text-sm font-medium">URL regulaminu</Label>
                            <Input
                                value={tosUrl}
                                onChange={(e) => setTosUrl(e.target.value)}
                                placeholder="https://www.wysko.pl/regulamin"
                            />
                        </div>

                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Label linku regulaminu</Label>
                            <Input
                                value={tosLinkLabel}
                                onChange={(e) => setTosLinkLabel(e.target.value)}
                                placeholder="Regulamin"
                            />
                            <p className="text-xs text-muted-foreground">
                                Klikalny tekst, który zastąpi placeholder w tekście regulaminu
                            </p>
                        </div>

                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Tekst newslettera</Label>
                            <Input
                                value={newsletterText}
                                onChange={(e) => setNewsletterText(e.target.value)}
                                placeholder="Chcę otrzymywać newsletter z poradami i nowościami"
                            />
                        </div>

                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleSaveCheckboxes}
                            disabled={savingCheckboxes || !checkboxesChanged}
                        >
                            {savingCheckboxes ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Zapisywanie...
                                </>
                            ) : (
                                'Zapisz teksty checkboxów'
                            )}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
